import { DateTime, Duration } from "luxon";
import { auth } from "../firebase";
import {
  BackendClient,
  DunningProcess,
  EmailDraft,
  ManualInvoice,
  PrepaidOrderWithCardTerminalRequest,
  PrepaidOrderWithCardTerminalResponse,
  Payment,
  ProcessPaymentInTerminalRequest,
  Invoice,
} from "../generated/backendClient";
import { Student, studentsProvider } from "../providers/studentsProvider";
import { sleep } from "../backoffice.utils";
import { toSnakeCase } from "../utils/string";
import type { GetListResult, RaRecord } from "react-admin";

const useLocalBackend = import.meta.env.VITE_USE_LOCAL_BACKEND || false;
const useProdEnv = !!parseInt(import.meta.env.VITE_USE_PRODUCTION_ENVIRONMENT);
const baseUrl = useProdEnv
  ? "https://backend.autovio.de"
  : useLocalBackend
    ? "http://localhost:8000"
    : location.hostname === "backoffice.autovio.de"
      ? "https://backend.autovio.de"
      : "https://backend.autovio.dev";

export async function getAuthenticatedBackendClient(e2eTest = false): Promise<BackendClient> {
  const jwt = await auth.currentUser?.getIdToken();
  return new BackendClient({ BASE: `${e2eTest ? "/pseudo-backend.autovio.dev" : baseUrl}/api`, TOKEN: jwt });
}

export function reactAdminToDRFPagination({ page, perPage }: { page: number; perPage: number }) {
  return {
    offset: (page - 1) * perPage,
    limit: perPage,
  };
}

export function reactAdminToDRFOrdering({ field, order }: { field: string; order: "ASC" | "DESC" }, snakeCase = true) {
  return `${order === "ASC" ? "" : "-"}${snakeCase ? toSnakeCase(field) : field}`;
}

type BackendResponseWithCountAndResults<T> = {
  count: number;
  results: T[];
  previous: string | null;
  next: string | null;
};

export function backendListResponseToRAGetListResult<T extends RaRecord>(
  response: BackendResponseWithCountAndResults<T>,
): GetListResult<T> {
  return {
    data: response.results,
    total: response.count,
    pageInfo: { hasPreviousPage: !!response.previous, hasNextPage: !!response.next },
  };
}

export async function createManualInvoice(data: ManualInvoice): Promise<void> {
  const backendClient = await getAuthenticatedBackendClient();
  await backendClient.order.orderManualInvoiceCreate({ requestBody: data });
}

export async function createPrepaidInvoiceForCardReader(
  data: PrepaidOrderWithCardTerminalRequest,
): Promise<PrepaidOrderWithCardTerminalResponse> {
  const backendClient = await getAuthenticatedBackendClient();
  return backendClient.order.orderPrepaidCardTerminalCreate({ requestBody: data });
}

export async function processPaymentInCardReader(id: string, data: ProcessPaymentInTerminalRequest): Promise<Payment> {
  const backendClient = await getAuthenticatedBackendClient();
  return backendClient.payment.paymentProcessInTerminalCreate({ id: id, requestBody: data });
}

export async function createPrepaidInvoiceAndPayWithCardReader(
  terminal_id: string,
  data: PrepaidOrderWithCardTerminalRequest,
): Promise<Payment | undefined> {
  const result = await createPrepaidInvoiceForCardReader(data);
  let payment = result.payment;

  const max_attempts = 3;
  const base_delay = Duration.fromMillis(1000);
  let attempts = 0;
  let delay: Duration | undefined;
  while (payment.state === "READY_FOR_CARD_TERMINAL" && attempts < max_attempts) {
    if (!delay) {
      delay = base_delay;
    } else {
      await sleep(delay);
      delay = delay.plus(delay); // exponential backoff
    }
    payment = await processPaymentInCardReader(payment.id!, { terminal_id: terminal_id });
    attempts += 1;
  }
  return payment;
}

export async function draftDunningNotice(
  student: Student,
  noticeType: "student_blocked" | "first_dunning_notice",
): Promise<{ dunningProcess: DunningProcess; draft: EmailDraft }>;
export async function draftDunningNotice(
  context: Student | DunningProcess,
  noticeType: "second_dunning_notice" | "inkasso_notice",
): Promise<{ dunningProcess: DunningProcess; draft: EmailDraft }>;
export async function draftDunningNotice(
  context: Student | DunningProcess,
  noticeType: "student_blocked" | "first_dunning_notice" | "second_dunning_notice" | "inkasso_notice",
): Promise<{ dunningProcess: DunningProcess; draft: EmailDraft }> {
  const backendClient = await getAuthenticatedBackendClient();
  let dunningProcess: DunningProcess;
  if (noticeType === "second_dunning_notice" || noticeType === "inkasso_notice") {
    dunningProcess = await backendClient.dunningProcess.dunningProcessRetrieve({
      id: isDunningProcess(context) ? context.id : context.dunningProcess!.id,
    });
  } else {
    dunningProcess = await backendClient.dunningProcess.dunningProcessPrepareForDraftRetrieve({
      studentId: (context as Student).autovioUserId,
    });
  }
  const draft = await backendClient.dunningProcess.dunningProcessDraftRetrieve({ id: dunningProcess.id, noticeType });
  return { dunningProcess, draft };
}

export async function cancelDunningProcess(id: string): Promise<DunningProcess> {
  const backendClient = await getAuthenticatedBackendClient();
  return backendClient.dunningProcess.dunningProcessPartialUpdate({ id, requestBody: { canceled: true } });
}

export async function postponeDunningProcess(id: string, studentId: string, postponedUntil: DateTime): Promise<void> {
  const backendClient = await getAuthenticatedBackendClient(window.e2eTest);
  const { data: student } = await studentsProvider.getOne("students", { id: studentId });

  if (student.dunningProcess) {
    const oldPostponedUntilMillis = student.dunningProcess.postponedUntil?.toMillis() ?? null;
    const updateReceived = new Promise<void>((resolve) => {
      const unsubscribe = studentsProvider.onUpdate(student, (updatedStudent) => {
        const updatedMillis = updatedStudent.dunningProcess?.postponedUntil?.toMillis() ?? null;
        if (updatedMillis !== oldPostponedUntilMillis) {
          unsubscribe();
          resolve();
        }
      });
    });
    await backendClient.dunningProcess.dunningProcessPartialUpdate({
      id,
      requestBody: { postponed_until: postponedUntil.toISODate() },
    });
    await updateReceived;
  }
}

export async function clearDunningProcessPostponement(id: string, studentId: string): Promise<void> {
  const backendClient = await getAuthenticatedBackendClient(window.e2eTest);
  const { data: student } = await studentsProvider.getOne("students", { id: studentId });

  if (student.dunningProcess?.postponedUntil) {
    const updateReceived = new Promise<void>((resolve) => {
      const unsubscribe = studentsProvider.onUpdate(student, (updatedStudent) => {
        const updatedPostponedUntil = updatedStudent.dunningProcess?.postponedUntil;
        if (!updatedPostponedUntil || updatedPostponedUntil <= DateTime.now()) {
          unsubscribe();
          resolve();
        }
      });
    });
    const clearDate = DateTime.now().minus({ days: 1 }).toISODate();
    await backendClient.dunningProcess.dunningProcessPartialUpdate({
      id,
      requestBody: { postponed_until: clearDate ?? undefined },
    });
    await updateReceived;
  }
}

export async function updateInvoice(id: string, requestBody: Partial<Invoice>): Promise<void> {
  const backendClient = await getAuthenticatedBackendClient(window.e2eTest);
  await backendClient.invoice.invoicePartialUpdate({
    id,
    requestBody,
  });
}

function isDunningProcess(x: any): x is DunningProcess {
  if (x && typeof x === "object") {
    const { id, student, invoices } = x;
    return typeof id === "string" && typeof student === "string" && Array.isArray(invoices);
  }
  return false;
}
